import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CreditCard, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { cart, loading: cartLoading } = useCart();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to checkout');
      navigate('/login');
      return;
    }

    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      navigate('/cart');
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout/create-session`, {
        origin_url: originUrl
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] pt-24 pb-24">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-12">
          <div className="bg-white rounded-xl p-12 shadow-sm border border-[#E5E5E7]">
            <CreditCard className="w-12 h-12 text-[#8E8E93] mx-auto mb-6" />
            <h1 className="text-2xl font-extrabold text-[#1C1C1E] mb-3">
              Sign in to checkout
            </h1>
            <p className="text-[#8E8E93] mb-8 text-sm">
              You need to be signed in to complete your purchase
            </p>
            <Link to="/login">
              <Button 
                className="rounded-lg bg-[#0F0F0F] text-white font-semibold px-8 py-4 hover:bg-[#2C2C2E]"
                data-testid="checkout-login-btn"
              >
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (cartLoading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] pt-24 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="h-96 bg-white rounded-xl skeleton"></div>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] pt-24 pb-24">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-12">
          <div className="bg-white rounded-xl p-12 shadow-sm border border-[#E5E5E7]">
            <CreditCard className="w-12 h-12 text-[#8E8E93] mx-auto mb-6" />
            <h1 className="text-2xl font-extrabold text-[#1C1C1E] mb-3">
              Your cart is empty
            </h1>
            <p className="text-[#8E8E93] mb-8 text-sm">
              Add some products before checking out
            </p>
            <Link to="/products">
              <Button 
                className="rounded-lg bg-[#FF4D00] text-white font-semibold px-8 py-4 hover:bg-[#CC3D00]"
              >
                Browse Products
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7] pt-24 pb-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/cart')}
          className="flex items-center gap-2 text-[#8E8E93] hover:text-[#1C1C1E] mb-8 transition-colors text-sm"
          data-testid="back-to-cart"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to cart
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Checkout Form */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E5E7]">
            <h1 className="text-xl font-bold text-[#1C1C1E] mb-6">
              Checkout
            </h1>

            <div className="space-y-4 mb-8">
              <div>
                <Label className="text-[#8E8E93] text-sm">Name</Label>
                <Input 
                  value={user?.name || ''}
                  disabled
                  className="rounded-lg bg-[#F5F5F7] border-0 mt-1"
                />
              </div>
              <div>
                <Label className="text-[#8E8E93] text-sm">Email</Label>
                <Input 
                  value={user?.email || ''}
                  disabled
                  className="rounded-lg bg-[#F5F5F7] border-0 mt-1"
                />
              </div>
            </div>

            <div className="border-t border-[#E5E5E7] pt-6">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="w-5 h-5 text-[#FF4D00]" />
                <span className="font-bold text-[#1C1C1E]">Payment</span>
              </div>
              <p className="text-[#8E8E93] text-sm mb-6">
                You'll be redirected to Stripe's secure checkout to complete your payment.
              </p>

              <Button 
                onClick={handleCheckout}
                disabled={loading}
                className="w-full rounded-lg bg-[#FF4D00] text-white font-bold py-5 hover:bg-[#CC3D00] transition-colors disabled:opacity-50"
                data-testid="pay-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>Pay ${cart.total.toFixed(2)}</>
                )}
              </Button>
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-[#0F0F0F] rounded-xl p-6 text-white">
            <h2 className="text-lg font-bold mb-6">
              Order Summary
            </h2>

            <div className="space-y-4 mb-6">
              {cart.items.map((item) => (
                <div key={item.product_id} className="flex gap-4">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#1C1C1E] shrink-0">
                    <img 
                      src={item.image_url} 
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{item.name}</p>
                    <p className="text-[#8E8E93] text-xs">Qty: {item.quantity}</p>
                  </div>
                  <span className="font-bold text-sm">${item.item_total.toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-[#2C2C2E] pt-4 space-y-2">
              <div className="flex justify-between text-[#8E8E93] text-sm">
                <span>Subtotal</span>
                <span>${cart.total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-[#8E8E93] text-sm">
                <span>Shipping</span>
                <span>Free</span>
              </div>
              <div className="flex justify-between text-lg font-bold pt-2">
                <span>Total</span>
                <span>${cart.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
